/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { CKEditor5 } from '@ckeditor/ckeditor5-metadata';
import { prepareEditorConfig } from '../prepareEditorConfig/prepareEditorConfig';
import { jsonFormatter } from '../jsonFormatter/jsonFormatter';
import { areTranslationsNeeded, getTranslationVariables } from '../translations/translations';
import {
	snippetEvaluationHeader,
	snippetEvaluationHeaderWithLink,
	snippetInitialContent,
	snippetInitialContentWithLink
} from '../../constants/editorContents';
import { getVariableName } from '../getVariableName/getVariableName';
import {
	AI_API_KEY,
	UPLOADCARE_PUBKEY,
	CLOUD_SERVICES_TOKEN_URL,
	CLOUD_SERVICES_WEBSOCKET_URL,
	LICENSE_KEY,
	DOCUMENT_ID
} from '../../constants/configPlaceholders';
import { CKEDITOR5_CSS_URL, CKEDITOR5_PREMIUM_FEATURES_CSS_URL } from '../../constants/cdns';
import type { PrepareEditorConfigForSnippetOptions } from './types';
import { requiresCloudServicesConfiguration } from '../requiresCloudServicesConfiguration/requiresCloudServicesConfiguration';
import { getFilename } from '../getFilename/getFilename';
import type { EditorPlugins } from '../../types/EditorPlugins';
import type { InstallMethod } from '../../constants/installation';
import { requiresExportCss } from '../requiresExportCss/requiresExportCss';
import { getEditorContainerClassNames } from '../getEditorContainerClassNames/getEditorContainerClassNames';

const DOCS_ROOT_URL = 'https://ckeditor.com/docs/ckeditor5/latest/';
const MENTIONS_DOCS_URL = DOCS_ROOT_URL + 'features/mentions.html';
const COMMENTS_WITH_MENTIONS_DOCS_URL = MENTIONS_DOCS_URL + '#comments-with-mentions';
const AI_CONFIG_DOCS_URL = DOCS_ROOT_URL + 'features/ai-assistant/ai-assistant-integration.html#amazon-bedrock-integration';
const EXPORT_PDF_CONFIG_DOCS_URL = DOCS_ROOT_URL + 'features/converters/export-pdf.html';
const EXPORT_WORD_CONFIG_DOCS_URL = DOCS_ROOT_URL + 'features/converters/export-word.html';
const EXPORT_INLINE_STYLES_CONFIG_DOCS_URL = DOCS_ROOT_URL + 'features/export-with-inline-styles.html';

export function prepareEditorConfigForSnippet( {
	editorOptions,
	selectedInstallMethod,
	selectedTechnology,
	isLoggedIn,
	hasPremiumPlugins,
	editorConfigContainers
}: PrepareEditorConfigForSnippetOptions ): string {
	const { editorLanguage, editorPlugins } = editorOptions;
	const shouldExportCss = requiresExportCss( { editorPlugins, selectedInstallMethod, selectedTechnology } );
	const cssFilename = getFilename( { type: shouldExportCss ? 'exportCss' : 'css', selectedTechnology } );

	const editorConfig = prepareEditorConfig(
		{
			...editorOptions,
			licenseKey: unquote( getVariableName( { LICENSE_KEY } ) ),
			aiAuthorizationToken: unquote( `'Bearer ' + ${ getVariableName( { AI_API_KEY } ) }` ),
			collaborationChannelId: unquote( getVariableName( { DOCUMENT_ID } ) ),
			cloudServicesTokenUrl: unquote( getVariableName( { CLOUD_SERVICES_TOKEN_URL } ) ),
			cloudServicesWebSocketUrl: unquote( getVariableName( { CLOUD_SERVICES_WEBSOCKET_URL } ) )
		},
		editorConfigContainers,
		{
			// To avoid mapping plugins to constructors, as we map values to the configuration object.
			pluginsToConstructors: currentEditorPlugins => currentEditorPlugins
		}
	);

	const stylesheetUrls = selectedInstallMethod === 'zip' ?
		[ './ckeditor5/ckeditor5.css', './ckeditor5-premium-features/ckeditor5-premium-features.css' ] :
		[ CKEDITOR5_CSS_URL, CKEDITOR5_PREMIUM_FEATURES_CSS_URL ];

	if ( !areTranslationsNeeded( editorLanguage ) ) {
		delete editorConfig.language;
	} else if ( selectedInstallMethod !== 'cdn' ) {
		const translations = getTranslationVariables( editorLanguage, editorPlugins );

		editorConfig.translations = unquote( `[${ translations }]` ) as unknown as CKEditor5.ArrayOrItem<CKEditor5.Translations>;
	}

	if ( editorConfig.extraPlugins ) {
		editorConfig.extraPlugins = editorConfig.extraPlugins.map( plugin => {
			return unquote( plugin.pluginName! ) as unknown as CKEditor5.PluginConstructor;
		} );
	}

	if ( editorPlugins.includes( 'Mention' ) ) {
		const getExampleMentionFeeds = ( docsUrl: string ) => ( {
			feeds: [
				{
					marker: '@',
					feed: [ unquote( `/* See: ${ docsUrl } */` ) ]
				}
			]
		} );

		editorConfig.mention = getExampleMentionFeeds( MENTIONS_DOCS_URL );

		if ( editorConfig.comments?.editorConfig ) {
			editorConfig.comments.editorConfig.mention = getExampleMentionFeeds( COMMENTS_WITH_MENTIONS_DOCS_URL );
		}
	}

	if ( editorPlugins.includes( 'MergeFields' ) ) {
		editorConfig.mergeFields = {
			// @ts-expect-error It's a placeholder to make a comment node inside an empty object.
			__placeholder: unquote( '/* Read more: https://ckeditor.com/docs/ckeditor5/latest/features/merge-fields.html#configuration */' )
		};
	}

	if ( editorPlugins.includes( 'CKBox' ) && !requiresCloudServicesConfiguration( editorPlugins ) ) {
		editorConfig.cloudServices = {
			tokenUrl: unquote( getVariableName( { CLOUD_SERVICES_TOKEN_URL } ) )
		};
	}

	if ( editorPlugins.includes( 'Uploadcare' ) ) {
		editorConfig.uploadcare = {
			pubkey: unquote( getVariableName( { UPLOADCARE_PUBKEY } ) )
		};
	}

	if ( editorPlugins.includes( 'AWSTextAdapter' ) ) {
		editorConfig.ai = {
			aws: {
				// @ts-expect-error It's a placeholder to make a comment node inside an empty object.
				__placeholder:
					unquote( `/* The Amazon Bedrock integration requires additional configuration. See: ${ AI_CONFIG_DOCS_URL } */` )
			},
			aiAssistant: {
				contentAreaCssClass: 'formatted'
			}
		};
	}

	editorConfig.initialData = getEditorInitialData( {
		editorPlugins,
		isLoggedIn,
		hasPremiumPlugins,
		selectedInstallMethod
	} );

	if ( editorPlugins.includes( 'ExportPdf' ) ) {
		editorConfig.exportPdf!.stylesheets = [
			unquote( '/* This path should point to the content stylesheets on your assets server. */', { removeTrailingComa: true } ),
			unquote( `/* See: ${ EXPORT_PDF_CONFIG_DOCS_URL } */`, { removeTrailingComa: true } ),
			`./${ cssFilename }`,
			unquote( '/* Export PDF needs access to stylesheets that style the content. */', { removeTrailingComa: true } ),
			...stylesheetUrls
		];
	}

	if ( editorPlugins.includes( 'ExportWord' ) ) {
		editorConfig.exportWord!.stylesheets = [
			unquote( '/* This path should point to the content stylesheets on your assets server. */', { removeTrailingComa: true } ),
			unquote( `/* See: ${ EXPORT_WORD_CONFIG_DOCS_URL } */`, { removeTrailingComa: true } ),
			`./${ cssFilename }`,
			unquote( '/* Export Word needs access to stylesheets that style the content. */', { removeTrailingComa: true } ),
			...stylesheetUrls
		];
	}

	if ( editorPlugins.includes( 'ExportInlineStyles' ) ) {
		editorConfig.exportInlineStyles ??= {};
		editorConfig.exportInlineStyles.stylesheets = [
			unquote( '/* This path should point to the content stylesheets on your assets server. */', { removeTrailingComa: true } ),
			unquote( `/* See: ${ EXPORT_INLINE_STYLES_CONFIG_DOCS_URL } */`, { removeTrailingComa: true } ),
			`./${ cssFilename }`,
			unquote( '/* Export inline styles needs access to stylesheets that style the content. */', { removeTrailingComa: true } ),
			...stylesheetUrls
		];
	}

	if ( editorPlugins.includes( 'EmailConfigurationHelper' ) ) {
		if ( editorPlugins.includes( 'Table' ) ) {
			const tablePropertiesOptions = {
				borderColors: unquote( 'DEFAULT_HEX_COLORS' ),
				backgroundColors: unquote( 'DEFAULT_HEX_COLORS' )
			};

			editorConfig.table ??= {};
			// @ts-expect-error Output will reference a variable.
			editorConfig.table.tableProperties = tablePropertiesOptions;
			// @ts-expect-error Output will reference a variable.
			editorConfig.table.tableCellProperties = tablePropertiesOptions;
		}

		if ( editorPlugins.includes( 'FontColor' ) ) {
			const fontColorOptions = {
				colorPicker: {
					format: 'hex'
				},
				colors: unquote( 'DEFAULT_HEX_COLORS' )
			};

			// @ts-expect-error Output will reference a variable.
			editorConfig.fontColor = fontColorOptions;
			// @ts-expect-error Output will reference a variable.
			editorConfig.fontBackgroundColor = fontColorOptions;
		}

		if ( editorPlugins.includes( 'ExportInlineStyles' ) ) {
			// @ts-expect-error Stringified function execution.
			editorConfig.exportInlineStyles.transformations = unquote( 'getEmailInlineStylesTransformations()' );
		}
	}

	if ( editorPlugins.includes( 'Fullscreen' ) ) {
		const classNames = getEditorContainerClassNames( editorOptions.editorType, editorPlugins )
			.split( ' ' )
			.concat( [ 'main-container' ] )
			.map( className => `'${ className }'` )
			.join( ',' );

		editorConfig.fullscreen = {
			// @ts-expect-error It's a placeholder to define a function inside an object.
			__placeholder: unquote( `onEnterCallback: container => container.classList.add( ${ classNames } )` )
		};
	}

	const { toolbar, plugins, extraPlugins, ...rest } = editorConfig;
	const sortedEditorConfig = { toolbar, plugins, extraPlugins, ...sortObjectKeys( rest ) };

	return JSON.stringify( sortedEditorConfig, jsonFormatter, 2 )
		.replaceAll( /"?__UNQUOTE__"?/g, '' )
		// To remove a comma at the end of a line that appears after replacing a string comment with a JavaScript comment node.
		.replaceAll( /__UNQUOTE_COMA__",?/g, '' )
		// To remove a temporary placeholder that is used to inject non-serializable data into the generated JSON output
		// (e.g. a JavaScript comment, a function, etc.).
		.replaceAll( /"__placeholder":/g, '' );
}

function unquote( str: string, { removeTrailingComa = false }: { removeTrailingComa?: boolean } = {} ): string {
	if ( removeTrailingComa ) {
		return `__UNQUOTE__${ str }__UNQUOTE_COMA__`;
	}

	return `__UNQUOTE__${ str }__UNQUOTE__`;
}

function sortObjectKeys( obj: Record<string, unknown> ): Record<string, unknown> {
	return Object.keys( obj )
		.sort()
		.reduce( ( result, key ) => {
			result[ key ] = obj[ key ];

			return result;
		}, {} as Record<string, unknown> );
}

function getEditorInitialData( {
	editorPlugins,
	isLoggedIn,
	hasPremiumPlugins,
	selectedInstallMethod
}: {
	editorPlugins: EditorPlugins;
	isLoggedIn: boolean;
	hasPremiumPlugins: boolean;
	selectedInstallMethod: InstallMethod;
} ): string {
	const hasLinkPlugin = editorPlugins.includes( 'Link' );
	const usesEvaluationLicense = !isLoggedIn && !hasPremiumPlugins && selectedInstallMethod === 'cdn';

	const initialDataParts: Array<string> = [];

	if ( usesEvaluationLicense ) {
		initialDataParts.push( hasLinkPlugin ? snippetEvaluationHeaderWithLink : snippetEvaluationHeader );
	}

	initialDataParts.push( hasLinkPlugin ? snippetInitialContentWithLink : snippetInitialContent );

	return initialDataParts.join( '\n' );
}
